import {PopoverPrimitive as Popover} from "ch3-ui-lib";
import {CalendarDays, ChevronLeft, ChevronRight, Clock} from "lucide-react";
import moment from "moment-timezone";
import {useEffect, useId, useRef, useState} from "react";
import {formatDate, formatTime} from "../Dnd/dndTime.ts";

const icon = '!p-0 shrink-0 !text-current';
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const HOURS = Array.from({length: 24}, (_, h) => h);
const minutesFor = (step: number) => Array.from({length: Math.floor(60 / step)}, (_, i) => i * step);

const popoverClass = 'z-[60] rounded-xl border border-solid border-line bg-surface p-3 text-ink shadow-overlay outline-none';
const fieldClass = 'box-border flex h-control-md w-full cursor-pointer items-center gap-2 rounded-control border border-solid border-fill-subtle bg-surface px-3 text-left text-callout text-ink outline-none hover:bg-surface-muted focus-visible:ring-2 focus-visible:ring-brand-focus data-[state=open]:ring-2 data-[state=open]:ring-brand-focus';

interface DateTimeFieldsProps {
    value: Date;
    onChange: (value: Date) => void;
    now: Date;
}

/** Date and Time fields (Dnd-GlobalCustom, SettingsStatus-Date); each opens its own popover. */
export const DateTimeFields = ({value, onChange, now}: DateTimeFieldsProps) => {
    const uid = useId();
    const [dateOpen, setDateOpen] = useState(false);
    const [timeOpen, setTimeOpen] = useState(false);

    const setDay = (day: Date) => {
        const next = moment(value).year(day.getFullYear()).month(day.getMonth()).date(day.getDate());
        onChange(next.toDate());
        setDateOpen(false);
    };
    const setTime = (hour: number, minute: number) => onChange(moment(value).hours(hour).minutes(minute).seconds(0).milliseconds(0).toDate());

    return (
        <div className="mt-3 flex gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <span id={`${uid}-date-label`} className="text-footnote font-medium text-ink-secondary">Date</span>
                <Popover.Root open={dateOpen} onOpenChange={setDateOpen}>
                    <Popover.Trigger asChild>
                        <button type="button" aria-labelledby={`${uid}-date-label ${uid}-date-value`} className={fieldClass}>
                            <CalendarDays size={16} aria-hidden="true" className={`${icon} !text-ink-muted`}/>
                            <span id={`${uid}-date-value`}>{formatDate(value)}</span>
                        </button>
                    </Popover.Trigger>
                    <Popover.Portal>
                        <Popover.Content align="start" sideOffset={6} className={`${popoverClass} w-[320px]`} aria-label="Choose date">
                            <Calendar selected={value} now={now} onSelect={setDay}/>
                        </Popover.Content>
                    </Popover.Portal>
                </Popover.Root>
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <span id={`${uid}-time-label`} className="text-footnote font-medium text-ink-secondary">Time</span>
                <Popover.Root open={timeOpen} onOpenChange={setTimeOpen}>
                    <Popover.Trigger asChild>
                        <button type="button" aria-labelledby={`${uid}-time-label ${uid}-time-value`} className={fieldClass}>
                            <Clock size={16} aria-hidden="true" className={`${icon} !text-ink-muted`}/>
                            <span id={`${uid}-time-value`} className="tabular-nums">{formatTime(value)}</span>
                        </button>
                    </Popover.Trigger>
                    <Popover.Portal>
                        <Popover.Content align="start" sideOffset={6} className={`${popoverClass} w-[180px]`} aria-label="Choose time">
                            <TimeColumns hour={value.getHours()} minute={value.getMinutes()} onChange={setTime}/>
                        </Popover.Content>
                    </Popover.Portal>
                </Popover.Root>
            </div>
        </div>
    );
};

/** Dnd-GlobalDate: month grid starting on Monday; past days are disabled, today is tinted. */
const Calendar = ({selected, now, onSelect}: { selected: Date; now: Date; onSelect: (day: Date) => void }) => {
    const [month, setMonth] = useState(() => moment(selected).startOf('month'));
    const today = moment(now).startOf('day');
    const lead = (month.isoWeekday() + 6) % 7;
    const days = Array.from({length: month.daysInMonth()}, (_, i) => month.clone().date(i + 1));
    const canGoBack = month.isAfter(today, 'month');

    return (
        <div>
            <div className="flex items-center px-1">
                <button type="button" aria-label="Previous month" disabled={!canGoBack} onClick={() => setMonth(month.clone().subtract(1, 'month'))}
                        className="flex size-11 cursor-pointer items-center justify-center rounded-control text-ink hover:bg-surface-muted disabled:cursor-default disabled:text-ink-disabled disabled:hover:bg-transparent">
                    <ChevronLeft size={18} aria-hidden="true" className={icon}/>
                </button>
                <h2 className="m-0 flex-1 text-center text-body-strong" aria-live="polite">{month.format('MMMM YYYY')}</h2>
                <button type="button" aria-label="Next month" onClick={() => setMonth(month.clone().add(1, 'month'))}
                        className="flex size-11 cursor-pointer items-center justify-center rounded-control text-ink hover:bg-surface-muted">
                    <ChevronRight size={18} aria-hidden="true" className={icon}/>
                </button>
            </div>
            <div className="px-1 pt-1">
                <div aria-hidden="true" className="grid grid-cols-7 pb-1 text-center text-caption text-ink-muted">
                    {WEEKDAYS.map((d) => <span key={d}>{d}</span>)}
                </div>
                <div className="grid grid-cols-7 gap-y-1">
                    {Array.from({length: lead}, (_, i) => <span key={`lead-${i}`}/>)}
                    {days.map((day) => {
                        const isPast = day.isBefore(today, 'day');
                        const isSelected = day.isSame(selected, 'day');
                        const isToday = day.isSame(today, 'day');
                        const tone = isSelected
                            ? 'bg-brand font-bold text-white'
                            : isToday
                                ? 'bg-brand-tint font-semibold text-brand-ink'
                                : isPast ? 'text-ink-disabled' : 'text-ink hover:bg-surface-muted';
                        return (
                            <button
                                key={day.date()}
                                type="button"
                                disabled={isPast}
                                aria-label={day.format('MMMM D, YYYY')}
                                aria-pressed={isSelected}
                                onClick={() => onSelect(day.toDate())}
                                className={`h-10 cursor-pointer rounded-chip text-callout disabled:cursor-default ${tone}`}
                            >
                                {day.date()}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

/** Dnd-GlobalTime: hour and minute columns (24-hour clock). */
const TimeColumns = ({hour, minute, step = 15, onChange}: { hour: number; minute: number; step?: number; onChange: (hour: number, minute: number) => void }) => {
    const minutesRef = useRef<HTMLDivElement>(null);
    const hoursRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Open with the selected hour in the middle of the list.
        const list = hoursRef.current;
        const item = list?.querySelector<HTMLElement>('[aria-pressed="true"]');
        if (list && item) list.scrollTop = item.offsetTop - list.clientHeight / 2 + item.clientHeight / 2;
        const mins = minutesRef.current;
        const minItem = mins?.querySelector<HTMLElement>('[aria-pressed="true"]');
        if (mins && minItem) mins.scrollTop = minItem.offsetTop - mins.clientHeight / 2 + minItem.clientHeight / 2;
    }, []);

    const cell = (selected: boolean) =>
        `flex h-8 shrink-0 cursor-pointer items-center justify-center rounded-chip text-callout tabular-nums ${selected ? 'bg-brand font-semibold text-white' : 'text-ink hover:bg-surface-muted'}`;

    return (
        <div className="flex gap-2">
            <div ref={hoursRef} role="group" aria-label="Hour" className="relative flex max-h-[204px] flex-1 flex-col gap-0.5 overflow-y-auto">
                {HOURS.map((h) => (
                    <button key={h} type="button" aria-pressed={h === hour} onClick={() => onChange(h, minute)} className={cell(h === hour)}>
                        {String(h).padStart(2, '0')}
                    </button>
                ))}
            </div>
            <div ref={minutesRef} role="group" aria-label="Minute" className="relative flex max-h-[204px] flex-1 flex-col gap-0.5 overflow-y-auto">
                {minutesFor(step).map((m) => (
                    <button key={m} type="button" aria-pressed={m === minute} onClick={() => onChange(hour, m)} className={cell(m === minute)}>
                        {String(m).padStart(2, '0')}
                    </button>
                ))}
            </div>
        </div>
    );
};

interface TimeFieldProps {
    hour: number;
    minute: number;
    onChange: (hour: number, minute: number) => void;
    /** Minutes between choices; 5 for working hours, 15 for DND and status. */
    step?: number;
    label?: string;
    invalid?: boolean;
    className?: string;
}

/** A single 24-hour time field with the hour / minute popover (working hours). */
export const TimeField = ({hour, minute, onChange, step = 15, label, invalid, className = ''}: TimeFieldProps) => {
    const [open, setOpen] = useState(false);
    const text = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <button type="button" aria-label={label ? `${label}: ${text}` : text} aria-invalid={invalid || undefined}
                        className={`${fieldClass} ${invalid ? '!border-danger-ink' : ''} ${className}`}>
                    <Clock size={16} aria-hidden="true" className={`${icon} !text-ink-muted`}/>
                    <span className="tabular-nums">{text}</span>
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content align="start" sideOffset={6} className={`${popoverClass} w-[180px]`} aria-label={label ?? 'Choose time'}>
                    <TimeColumns hour={hour} minute={minute} step={step} onChange={(h, m) => onChange(h, m)}/>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};
